import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Link, useParams } from 'react-router-dom';
import remarkGfm from 'remark-gfm';
import PublicLayout from './common/PublicLayout';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import CTA from '../components/CTA';
import { getMetadataAndContent } from '../misc/markdown-utils';
import MiddleLoader from '../components/common/MiddleLoader';
import NoMatch from './NoMatch';
import SEO from '../components/common/SEO';

const components = {
  // eslint-disable-next-line
  a: (props) => <a target="_blank" rel="follow" {...props} />,

  // Use the title attribute to add inline styles to images
  // eslint-disable-next-line
  img: ({ alt, title, ...props}) => {
    let style;
    if (title) {
      style = title.split(/;\s*/g).reduce((acc, curr) => {
        const [key, value] = curr.split(/:\s*/g);
        if (!key || !value) {
          return acc;
        }
        return { ...acc, [key]: value };
      }, {});
    }

    return (
      <img
        className="max-w-full h-auto mx-auto"
        alt={alt}
        title={alt}
        style={style}
        {...props}
      />);
  },
}


export default function BlogPostPage() {
  const { slug } = useParams();
  const [postContent, setPostContent] = useState('');
  const [metadata, setMetadata] = useState({});
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    const fetchPostContent = async () => {
      try {
        const module = await import(`../blog/${slug}.md`);
        const response = await fetch(module.default);
        const text = await response.text();
        const [metadata, content] = getMetadataAndContent(text);

        setPostContent(content);
        setMetadata(metadata);
        setLoading(false);
      } catch (error) {
        console.error(error);
        setLoading(false);
      }
    };

    fetchPostContent();
  }, [slug]);

  if (loading) {
    return <MiddleLoader />;
  }

  if (postContent === '') {
    return <NoMatch />;
  }

  return (
    <PublicLayout className="my-5">
      <SEO
        title={metadata.title}
        description={metadata.description}
        linkedData={metadata.linkedData || {
          '@type': 'BlogPosting',
          headline: metadata.title,
          description: metadata.description,
          author: {
            '@type': 'Person',
            name: metadata.author
          },
          datePublished: metadata.date,
          image: metadata.imageUrl ? `${window.location.origin}${metadata.imageUrl}` : undefined,
          keywords: metadata.keywords,
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `${window.location.origin}${metadata.path}`
          }
        }}
      />

      <article className="max-w-screen-md mx-auto mb-16 text-gray-500 px-2">
        <Link
          to="/blog"
          className="btn-flat flex items-center gap-2"
        >
          <ArrowLeftIcon className="w-5 h-5" aria-hidden="true" />
          Blog home
        </Link>

        <h1 className="text-black text-5xl py-5">{ metadata.title }</h1>
        <ReactMarkdown
          className="prose"
          children={postContent}
          components={components}
          remarkPlugins={[remarkGfm]}
        />
      </article>

      {/*<Pricing/>*/}
      <CTA />
    </PublicLayout>
  );
}
